/**
 * @fileoverview Custom hook for querying app version information and update availability.
 */

import {useState} from "react";
import {useQuery} from "react-query";

import {AppService, getAppVersion} from "../services/AppService";

/**
 * Custom hook that queries the app version and checks for available updates.
 * Automatically refetches every 60 seconds to check for new versions.
 *
 * @returns {Object} Query result containing version information and update availability.
 * @returns {string} returns.currentVersion The current app version.
 * @returns {string} returns.latestVersion The latest available version.
 * @returns {boolean} returns.updateAvailable Whether an update is available.
 */
export const useAppVersionQuery = () => {
  const [currentVersion] = useState(getAppVersion(document));

  return useQuery(
    "hasUpdateAvailable",
    async () => {
      const {
        data: {appVersion},
      } = await AppService.getAvailableAppVersion();

      return {
        currentVersion,
        latestVersion: appVersion,
        updateAvailable: appVersion ? currentVersion !== appVersion : false,
      };
    },
    {
      refetchInterval: 60000,
      placeholderData: {
        currentVersion,
        latestVersion: currentVersion,
        updateAvailable: false,
      },
    },
  );
};
